import { useEffect, useMemo, useState } from 'react';
import type { Instance, Person, SessionPlan } from '@gm-binder/shared';
import { api } from '../../api/client';
import { Modal, Button, Field, Input, Textarea, IconButton, Badge, LoadingState } from '../ui';
import {
  formatSlot,
  toDatetimeLocal,
  fromDatetimeLocal,
} from '../../lib/datetime';
import { hasPhone, smsLink, whatsappLink } from '../../lib/contact';
import { useUiStore } from '../../store/uiStore';
import styles from './SessionTools.module.css';

interface SlotDraft {
  id?: string;
  local: string;
  durationMin: string;
  note: string;
}

function defaultSlotLocal(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setHours(20, 30, 0, 0);
  return toDatetimeLocal(d.toISOString());
}

/** Plan the next session: propose slots, share a booking link, read availability, confirm. */
export function SchedulingModal({ instance, onClose }: { instance: Instance; onClose: () => void }) {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<SessionPlan | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [slots, setSlots] = useState<SlotDraft[]>([]);
  const [busy, setBusy] = useState(false);
  const toast = useUiStore((s) => s.toast);

  const syncFromPlan = (p: SessionPlan) => {
    setTitle(p.title);
    setLocation(p.location);
    setNotes(p.notes);
    setSlots(
      p.slots.map((s) => ({
        id: s.id,
        local: toDatetimeLocal(s.start),
        durationMin: s.durationMin ? String(s.durationMin) : '',
        note: s.note ?? '',
      })),
    );
  };

  useEffect(() => {
    void (async () => {
      try {
        const [plans, ppl] = await Promise.all([
          api.listSessionPlans(instance.id),
          api.listPeople(),
        ]);
        setPeople(ppl);
        if (plans.length) {
          setPlan(plans[0]);
          syncFromPlan(plans[0]);
        }
      } catch {
        toast('Caricamento fallito', 'danger');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance.id]);

  const publicLink = useMemo(
    () => (plan ? `${window.location.origin}/book/${plan.publicToken}` : ''),
    [plan],
  );

  const createPlan = async () => {
    setBusy(true);
    try {
      const p = await api.createSessionPlan({
        instanceId: instance.id,
        slots: [{ start: fromDatetimeLocal(defaultSlotLocal()) }],
      });
      setPlan(p);
      syncFromPlan(p);
    } catch {
      toast('Creazione fallita', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const saveDetails = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      const updated = await api.updateSessionPlan(plan.id, {
        title: title.trim(),
        location: location.trim(),
        notes: notes.trim(),
        slots: slots
          .filter((s) => s.local)
          .map((s) => ({
            id: s.id,
            start: fromDatetimeLocal(s.local),
            durationMin: s.durationMin ? Number(s.durationMin) : undefined,
            note: s.note.trim() || undefined,
          })),
      });
      setPlan(updated);
      syncFromPlan(updated);
      toast('Piano salvato', 'success');
    } catch {
      toast('Salvataggio fallito', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const patch = async (data: Parameters<typeof api.updateSessionPlan>[1]) => {
    if (!plan) return;
    setBusy(true);
    try {
      const updated = await api.updateSessionPlan(plan.id, data);
      setPlan(updated);
      syncFromPlan(updated);
    } catch {
      toast('Aggiornamento fallito', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const removePlan = async () => {
    if (!plan || !confirm('Eliminare questo piano sessione? Il link non funzionerà più.')) return;
    setBusy(true);
    try {
      await api.deleteSessionPlan(plan.id);
      setPlan(null);
      setSlots([]);
      toast('Piano eliminato', 'info');
    } catch {
      toast('Eliminazione fallita', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      toast('Link copiato', 'success');
    } catch {
      toast('Copia non riuscita', 'warn');
    }
  };

  // Roster: people linked to a PG in this run, for one-tap sharing.
  const roster = useMemo(() => {
    const byId = new Map(people.map((p) => [p.id, p] as const));
    return instance.players
      .filter((pl) => pl.personId && byId.has(pl.personId))
      .map((pl) => ({ person: byId.get(pl.personId!)!, characterName: pl.characterName }));
  }, [people, instance.players]);

  const shareMessage = `Ciao! Per la prossima sessione di "${instance.name}" dimmi quando sei disponibile: ${publicLink}`;

  return (
    <Modal open onClose={onClose} title="📅 Pianifica la prossima sessione" width={620}>
      {loading ? (
        <LoadingState />
      ) : !plan ? (
        <div className={styles.stack}>
          <p className={styles.hint}>
            Crea un piano per proporre delle date ai giocatori. Riceverai un link da condividere:
            ognuno indicherà il proprio personaggio e la disponibilità.
          </p>
          <Button variant="primary" onClick={() => void createPlan()} disabled={busy}>
            Crea piano sessione
          </Button>
        </div>
      ) : (
        <div className={styles.stack}>
          {/* Public link */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Link per i giocatori</h3>
            <div className={styles.linkRow}>
              <input className={styles.linkInput} readOnly value={publicLink} onFocus={(e) => e.target.select()} />
              <Button variant="default" onClick={() => void copyLink()}>
                Copia
              </Button>
              <IconButton label="Apri" onClick={() => window.open(publicLink, '_blank')}>
                ↗
              </IconButton>
            </div>
            {roster.length > 0 ? (
              <div>
                {roster.map(({ person, characterName }) => (
                  <div key={person.id} className={styles.shareItem}>
                    <span>
                      {person.name} <span className={styles.matrixNames}>· {characterName}</span>
                    </span>
                    <span className={styles.shareLinks}>
                      {hasPhone(person.phone) ? (
                        <>
                          <a
                            className={styles.chip}
                            href={whatsappLink(person.phone, shareMessage)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            WhatsApp
                          </a>
                          <a className={styles.chip} href={smsLink(person.phone, shareMessage)}>
                            SMS
                          </a>
                        </>
                      ) : (
                        <span className={styles.matrixNames}>nessun numero</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.hint}>
                Collega i giocatori ai PG (pulsante 👥) per condividere il link con un tocco.
              </p>
            )}
          </div>

          {/* Details */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Dettagli</h3>
            <Field label="Titolo">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field label="Luogo">
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Casa di Marco / Discord / …"
              />
            </Field>
            <Field label="Note">
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
            </Field>
          </div>

          {/* Slots */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Date proposte</h3>
            {slots.map((slot, i) => (
              <div key={slot.id ?? `new-${i}`} className={styles.slotRow}>
                <input
                  type="datetime-local"
                  value={slot.local}
                  onChange={(e) =>
                    setSlots((arr) => arr.map((s, j) => (j === i ? { ...s, local: e.target.value } : s)))
                  }
                />
                <input
                  type="number"
                  min={0}
                  placeholder="min"
                  value={slot.durationMin}
                  onChange={(e) =>
                    setSlots((arr) => arr.map((s, j) => (j === i ? { ...s, durationMin: e.target.value } : s)))
                  }
                />
                <IconButton
                  label="Rimuovi data"
                  onClick={() => setSlots((arr) => arr.filter((_, j) => j !== i))}
                >
                  🗑️
                </IconButton>
              </div>
            ))}
            <div>
              <Button
                variant="ghost"
                onClick={() =>
                  setSlots((arr) => [...arr, { local: defaultSlotLocal(), durationMin: '', note: '' }])
                }
              >
                ＋ Aggiungi data
              </Button>
            </div>
            <div className={styles.actionsRight}>
              <Button variant="primary" onClick={() => void saveDetails()} disabled={busy}>
                Salva piano
              </Button>
            </div>
          </div>

          {/* Responses + confirm */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              Disponibilità ({plan.responses.length}{' '}
              {plan.responses.length === 1 ? 'risposta' : 'risposte'})
            </h3>
            {plan.slots.length === 0 && <p className={styles.hint}>Aggiungi e salva almeno una data.</p>}
            {plan.slots.map((slot) => {
              const yes = plan.responses.filter((r) =>
                r.slots.some((s) => s.slotId === slot.id && s.availability === 'yes'),
              );
              const maybe = plan.responses.filter((r) =>
                r.slots.some((s) => s.slotId === slot.id && s.availability === 'maybe'),
              );
              const confirmed = plan.confirmedSlotId === slot.id;
              return (
                <div key={slot.id} className={styles.matrixRow}>
                  <div className={styles.matrixHead}>
                    <span className={styles.matrixWhen}>
                      {formatSlot(slot.start, slot.durationMin)}{' '}
                      {confirmed && <Badge tone="success">Confermata</Badge>}
                    </span>
                    <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                      <Badge tone="success">✓ {yes.length}</Badge>
                      {maybe.length > 0 && <Badge tone="warn">~ {maybe.length}</Badge>}
                      <Button
                        size="sm"
                        variant={confirmed ? 'ghost' : 'default'}
                        onClick={() =>
                          void patch(
                            confirmed
                              ? { confirmedSlotId: null, status: 'open' }
                              : { confirmedSlotId: slot.id, status: 'scheduled' },
                          )
                        }
                      >
                        {confirmed ? 'Annulla' : 'Conferma'}
                      </Button>
                    </span>
                  </div>
                  {(yes.length > 0 || maybe.length > 0) && (
                    <div className={styles.matrixNames}>
                      {yes.map((r) => r.characterName).join(', ')}
                      {maybe.length > 0 && ` · forse: ${maybe.map((r) => r.characterName).join(', ')}`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.actions}>
            <Button variant="ghost" onClick={() => void removePlan()} disabled={busy}>
              Elimina piano
            </Button>
            <div className={styles.actionsRight}>
              <Button
                variant="default"
                onClick={() => void patch({ status: plan.status === 'closed' ? 'open' : 'closed' })}
                disabled={busy}
              >
                {plan.status === 'closed' ? 'Riapri risposte' : 'Chiudi risposte'}
              </Button>
              <Button variant="primary" onClick={onClose}>
                Fatto
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
